import { useEffect, useState } from "react";
import { Archive, Download, Flag, Mail, Paperclip, Reply, ReplyAll, Share, Trash2 } from "lucide-react";
import { Badge, Button, Spinner } from "@discordforge/ui";
import { downloadUrl } from "../api";
import type { Attachment, Message } from "../types";
import { formatDate, formatSize, sanitizeHtml, senderEmail, senderName } from "../utils";

/* Fetch inline attachments (cId) as object URLs so `cid:` <img> references in the
 * HTML body render. Key = bare content id (stripped of `cid:` prefix and <>). */
function useCidImages(part: Message, accountHash: string): Record<string, string> {
    const [urls, setUrls] = useState<Record<string, string>>({});
    useEffect(() => {
        if (!part.html) {
            setUrls({});
            return;
        }
        const atts = (part.attachments?.["@Collection"] ?? []).filter((a) => a.isInline && a.cId);
        if (!atts.length) {
            setUrls({});
            return;
        }
        let cancelled = false;
        const jobs = atts.map((a) => ({
            key: a.cId.replace(/^cid:/i, "").replace(/[<>]/g, "").toLowerCase(),
            url: downloadUrl(a, accountHash),
        }));
        Promise.all(
            jobs.map(async ({ key, url }) => {
                try {
                    const res = await fetch(url, { credentials: "same-origin" });
                    if (!res.ok) return null;
                    return { key, blobUrl: URL.createObjectURL(await res.blob()) };
                } catch {
                    return null;
                }
            })
        ).then((results) => {
            if (cancelled) return;
            const map: Record<string, string> = {};
            for (const r of results) if (r) map[r.key] = r.blobUrl;
            setUrls(map);
        });
        return () => {
            cancelled = true;
        };
    }, [part.folder, part.uid, part.html, accountHash]);
    return urls;
}

/* 1×1 transparent GIF — placeholder for cid: images that are still loading or
 * failed, so the browser never tries to load an unknown `cid:` scheme. */
const PIXEL_GIF = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function renderMessageHtml(html: string, cidUrls: Record<string, string>): string {
    let out = html;
    for (const [key, url] of Object.entries(cidUrls)) {
        out = out.replace(new RegExp(`cid:${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "gi"), url);
    }
    /* remaining cid: refs (not yet fetched or failed) → pixel, avoids
     * ERR_UNKNOWN_URL_SCHEME on first paint before substitution completes */
    out = out.replace(/src="cid:[^"]*"/gi, `src="${PIXEL_GIF}"`);
    return sanitizeHtml(out);
}

export interface MessageViewerProps {
    message: Message;
    /** Items of the conversation thread (may be list-level, without bodies). */
    conversation?: Message[];
    conversationLoading?: boolean;
    accountHash: string;
    loading: boolean;
    error: string;
    onClose: () => void;
    /** Open another message of the thread (navigate to it). */
    onOpenMessage?: (folder: string, uid: number) => void;
    /** Start compose prefilled (reply | replyall | forward). */
    onCompose?: (mode: "reply" | "replyall" | "forward", message: Message) => void;
    /** Delete (to Trash, or forever when in Trash). */
    onDelete?: (message: Message) => void;
    /** Archive the message. */
    onArchive?: (message: Message) => void;
    /** Toggle the ★ flag. */
    onToggleFlag?: (message: Message) => void;
}

export function MessageViewer({
    message,
    conversation,
    conversationLoading = false,
    accountHash,
    loading,
    error,
    onClose,
    onOpenMessage,
    onCompose,
    onDelete,
    onArchive,
    onToggleFlag,
}: MessageViewerProps) {
    if (loading) {
        return (
            <section className="flex h-full items-center justify-center bg-[#0b0b0f]">
                <Spinner size={28} label="Loading message…" />
            </section>
        );
    }

    if (error) {
        return (
            <section className="flex h-full items-center justify-center bg-[#0b0b0f] p-8">
                <p className="text-sm font-medium text-red-400">{error}</p>
            </section>
        );
    }

    const isUnseen = !(message.flags ?? []).includes("\\seen");
    const isFlagged = (message.flags ?? []).includes("\\flagged");
    const parts: Message[] =
        conversation && conversation.length > 1 ? conversation : [message];
    const showConversation = parts.length > 1;

    return (
        <section className="custom-scrollbar flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-[#0b0b0f]">
            {/* Header */}
            <header className="shrink-0 border-b border-white/10 px-6 pb-4 pt-5">
                <div className="flex items-start justify-between gap-4">
                    <h1
                        className={`text-lg leading-snug tracking-tight ${
                            isUnseen ? "font-black text-white" : "font-bold text-zinc-100"
                        }`}
                    >
                        {message.subject || "(no subject)"}
                    </h1>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                {showConversation && (
                    <div className="mt-2 text-[11px] font-black uppercase tracking-[0.15em] text-orange-400">
                        Conversation · {parts.length} messages
                    </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="dark"
                        onClick={() => onCompose?.("reply", message)}
                        title="Reply"
                    >
                        <Reply size={13} /> Reply
                    </Button>
                    <Button
                        size="sm"
                        variant="dark"
                        onClick={() => onCompose?.("replyall", message)}
                        title="Reply all"
                    >
                        <ReplyAll size={13} /> Reply all
                    </Button>
                    <Button
                        size="sm"
                        variant="dark"
                        onClick={() => onCompose?.("forward", message)}
                        title="Forward"
                    >
                        <Share size={13} /> Forward
                    </Button>
                    {onToggleFlag && (
                        <Button
                            size="sm"
                            variant="dark"
                            onClick={() => onToggleFlag(message)}
                            title={isFlagged ? "Unflag" : "Flag"}
                        >
                            <Flag size={13} className={isFlagged ? "fill-amber-400 text-amber-400" : ""} />
                            {isFlagged ? "Unflag" : "Flag"}
                        </Button>
                    )}
                    {onArchive && (
                        <Button size="sm" variant="dark" onClick={() => onArchive(message)} title="Archive">
                            <Archive size={13} />
                        </Button>
                    )}
                    {onDelete && (
                        <Button size="sm" variant="danger" onClick={() => onDelete(message)} title="Delete">
                            <Trash2 size={13} />
                        </Button>
                    )}
                    {message.isSpam && <Badge variant="danger">Spam</Badge>}
                    {message.encrypted && <Badge variant="amber">Encrypted</Badge>}
                </div>
            </header>

            {/* Conversation body */}
            <article className="min-h-0 flex-1 px-6 py-6">
                {conversationLoading && showConversation ? (
                    <div className="flex items-center justify-center py-10">
                        <Spinner size={20} label="Loading conversation…" />
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {parts.map((part, i) => (
                            <div key={`${part.folder}/${part.uid}`} className={i > 0 ? "mt-8" : ""}>
                                {/* part divider */}
                                {i > 0 && (
                                    <div className="mb-6 flex items-center gap-3">
                                        <div className="h-px flex-1 bg-white/10" />
                                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-600">
                                            Earlier message
                                        </span>
                                        <div className="h-px flex-1 bg-white/10" />
                                    </div>
                                )}

                                <MessagePart
                                    part={part}
                                    isCurrent={part.uid === message.uid}
                                    accountHash={accountHash}
                                    onOpenMessage={onOpenMessage}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </article>
        </section>
    );
}

function MessagePart({
    part,
    isCurrent,
    accountHash,
    onOpenMessage,
}: {
    part: Message;
    isCurrent: boolean;
    accountHash: string;
    onOpenMessage?: (folder: string, uid: number) => void;
}) {
    const cidUrls = useCidImages(part, accountHash);
    const fromName = senderName(part.from ?? []);
    const fromEmail = senderEmail(part.from ?? []);
    const toList = (part.to ?? []).map((t) => t.email).filter(Boolean).join(", ");
    const attachments = (part.attachments?.["@Collection"] ?? []).filter((a: Attachment) => !a.isInline);

    return (
        <div className={isCurrent ? "rounded-2xl border border-orange-500/15 bg-orange-500/[0.03] p-4" : "p-4"}>
            {/* From / To */}
            <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0e0e11] text-sm font-black text-orange-400 ring-1 ring-white/10">
                    {fromName[0]?.toUpperCase() ?? "?"}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-bold text-zinc-100">{fromName}</span>
                        {isCurrent && <Badge variant="accent">current</Badge>}
                        {part.from?.[0]?.dkimStatus === "pass" && <Badge variant="success">✓ dkim</Badge>}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-zinc-500">
                        {fromEmail}
                        {toList && <span className="text-zinc-600"> · to: {toList}</span>}
                    </div>
                    <div className="mt-0.5 text-[11px] tabular-nums text-zinc-500">
                        {formatDate(part.dateTimestamp)} · {formatSize(part.size)}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="mt-4">
                {part.html || part.plain ? (
                    part.html ? (
                        <div
                            className="mail-body text-[13.5px] leading-relaxed text-zinc-200 [&_a]:text-orange-400 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-xl [&_blockquote]:border-l-2 [&_blockquote]:border-white/15 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-400 [&_p]:my-2"
                            dangerouslySetInnerHTML={{ __html: renderMessageHtml(part.html, cidUrls) }}
                        />
                    ) : (
                        <pre className="whitespace-pre-wrap font-sans text-[13.5px] leading-relaxed text-zinc-200">
                            {part.plain}
                        </pre>
                    )
                ) : isCurrent ? (
                    <div className="flex flex-col items-center gap-2 py-6 text-center">
                        <Mail size={22} className="text-zinc-600" />
                        <p className="text-sm text-zinc-500">This message has no readable body.</p>
                    </div>
                ) : (
                    <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
                        <span className="truncate text-xs text-zinc-500">
                            Part of the conversation — open to read the full message.
                        </span>
                        {onOpenMessage && (
                            <button
                                type="button"
                                onClick={() => onOpenMessage(part.folder, part.uid)}
                                className="shrink-0 cursor-pointer rounded-lg bg-white/5 px-2.5 py-1 text-xs font-semibold text-zinc-300 transition-colors hover:bg-orange-500/15 hover:text-orange-300"
                            >
                                Open message
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
                <div className="mt-4">
                    <div className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em] text-zinc-500">
                        <Paperclip size={12} /> Attachments ({attachments.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {attachments.map((a) => (
                            <a
                                key={a.mimeIndex}
                                href={downloadUrl(a, accountHash)}
                                download={a.fileName}
                                className="group flex cursor-pointer items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 transition-colors hover:border-orange-500/30 hover:bg-orange-500/5"
                            >
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/10 text-orange-400">
                                    <Paperclip size={13} />
                                </span>
                                <span className="min-w-0">
                                    <span className="block max-w-52 truncate text-xs font-semibold text-zinc-200 group-hover:text-white">
                                        {a.fileName}
                                    </span>
                                    <span className="block text-[10px] tabular-nums text-zinc-500">
                                        {a.mimeType} · {formatSize(a.estimatedSize)}
                                    </span>
                                </span>
                                <Download size={13} className="shrink-0 text-zinc-600 transition-colors group-hover:text-orange-400" />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
